import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/lib/AuthContext";
import { CountryProvider } from "@/lib/CountryContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import OfflineBanner from "@/components/OfflineBanner";
import AuthModal from "@/components/AuthModal";
import { Toaster } from "@/components/ui/sonner";
import ProtectedRoute from "@/components/ProtectedRoute";
import HomePage from "@/pages/HomePage";
import AnnoncesPage from "@/pages/AnnoncesPage";
import ImmobilierPage from "@/pages/ImmobilierPage";
import LocationMeubleePage from "@/pages/LocationMeubleePage";
import BonnesAffairesPage from "@/pages/BonnesAffairesPage";
import AnnonceDetailPage from "@/pages/AnnonceDetailPage";
import DeposerPage from "@/pages/DeposerPage";
import HotelleriesPage from "@/pages/HotelleriesPage";
import HotelDetailPage from "@/pages/HotelDetailPage";
import FavorisPage from "@/pages/FavorisPage";
import AbonnementsPage from "@/pages/AbonnementsPage";
import AbonnementsProPage from "@/pages/AbonnementsProPage";
import AbonnementsProRetourPage from "@/pages/AbonnementsProRetourPage";
import DashboardPage from "@/pages/DashboardPage";
import CguPage from "@/pages/CguPage";
import ConnexionPage from "@/pages/ConnexionPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import ContactPage from "@/pages/ContactPage";
import AuthCallback from "@/pages/AuthCallback";
import MonComptePage from "@/pages/MonComptePage";
import MesAnnoncesPage from "@/pages/MesAnnoncesPage";
import MessagesPage from "@/pages/MessagesPage";
import ConversationPage from "@/pages/ConversationPage";
import ParametresPage from "@/pages/ParametresPage";
import AdminHotelsPage from "@/pages/AdminHotelsPage";
import AdminHotelAvailabilityPage from "@/pages/AdminHotelAvailabilityPage";
import TarifExpressPage from "@/pages/TarifExpressPage";
import HotelReservationStatusPage from "@/pages/HotelReservationStatusPage";
import ReviewPage from "@/pages/ReviewPage";
import HotelParHeurePage from "@/pages/HotelParHeurePage";
import DayUseOptinPage from "@/pages/DayUseOptinPage";
import AdminAnnoncesPage from "@/pages/AdminAnnoncesPage";
import AdminDashboardPage from "@/pages/AdminDashboardPage";
import AdminSecurityPage from "@/pages/AdminSecurityPage";
import AdminInvestmentsPage from "@/pages/AdminInvestmentsPage";
import AdminCampagnesPage from "@/pages/AdminCampagnesPage";
import AdminGuideFeedbackPage from "@/pages/AdminGuideFeedbackPage";
import AdminFinancingPage from "@/pages/AdminFinancingPage";
import AdminPaymentsPage from "@/pages/AdminPaymentsPage";
import AdminEscrowsPage from "@/pages/AdminEscrowsPage";
import EscrowStatusPage from "@/pages/EscrowStatusPage";
import MesTransactionsPage from "@/pages/MesTransactionsPage";
import OffreLancementHotelleriePage from "@/pages/OffreLancementHotelleriePage";
import VenduOneClickPage from "@/pages/VenduOneClickPage";
import AdminSmsPage from "@/pages/AdminSmsPage";
import ChoisirAbonnementPage from "@/pages/ChoisirAbonnementPage";
import { BiometricInviteBanner } from "@/components/BiometricInviteBanner";
import { PresenceHeartbeat } from "@/components/PresenceDot";
import FaqPaiementsPage from "@/pages/FaqPaiementsPage";
import ConfianceSecuritePage from "@/pages/ConfianceSecuritePage";
import PolitiqueConfidentialitePage from "@/pages/PolitiqueConfidentialitePage";
import { RgpdBanner } from "@/components/RgpdBanner";
import ComparePage from "@/pages/ComparePage";
import SellerProfilePage from "@/pages/SellerProfilePage";
import MesAlertesPage from "@/pages/MesAlertesPage";
import NotFoundPage from "@/pages/NotFoundPage";
import { CompareFloatingDrawer } from "@/components/CompareToggleButton";
import CountryStripe from "@/components/CountryStripe";

function AppRouter() {
  const location = useLocation();
  // CRITICAL: handle OAuth callback synchronously during render
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <Navbar />
      <CountryStripe />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/annonces" element={<AnnoncesPage />} />
        <Route path="/immobilier" element={<ImmobilierPage />} />
        <Route path="/location-meublee" element={<LocationMeubleePage />} />
        <Route path="/bonnes-affaires" element={<BonnesAffairesPage />} />
        <Route path="/annonces/:id" element={<AnnonceDetailPage />} />
        <Route path="/deposer" element={<DeposerPage />} />
        <Route path="/hotelleries" element={<HotelleriesPage />} />
        <Route path="/hotelleries/offre-lancement" element={<OffreLancementHotelleriePage />} />
        <Route path="/hotelleries/:id" element={<HotelDetailPage />} />
        <Route path="/tarif-express/:token" element={<TarifExpressPage />} />
        <Route path="/hotelleries/:id/reservation/:ref" element={<HotelReservationStatusPage />} />
        <Route path="/avis/:token" element={<ReviewPage />} />
        <Route path="/hotel-par-heure" element={<HotelParHeurePage />} />
        <Route path="/activer-hotel-par-heure/:token" element={<DayUseOptinPage />} />
        <Route path="/favoris" element={<FavorisPage />} />
        <Route path="/abonnements" element={<AbonnementsPage />} />
        <Route path="/abonnements/pro" element={<AbonnementsProPage />} />
        <Route path="/choisir-abonnement" element={<ChoisirAbonnementPage />} />
        <Route path="/abonnements/pro/retour" element={<AbonnementsProRetourPage />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/cgu" element={<CguPage />} />
        <Route path="/faq-paiements" element={<FaqPaiementsPage />} />
        <Route path="/confiance-securite" element={<ConfianceSecuritePage />} />
        <Route path="/confidentialite" element={<PolitiqueConfidentialitePage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/connexion" element={<ConnexionPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/comparer" element={<ComparePage />} />
        <Route path="/vendeurs/:userId" element={<SellerProfilePage />} />
        {/* Auth-only */}
        <Route path="/mon-compte" element={<ProtectedRoute><MonComptePage /></ProtectedRoute>} />
        <Route path="/mes-annonces" element={<ProtectedRoute><MesAnnoncesPage /></ProtectedRoute>} />
        <Route path="/mes-transactions" element={<ProtectedRoute><MesTransactionsPage /></ProtectedRoute>} />
        <Route path="/escrow/:ref" element={<EscrowStatusPage />} />
        <Route path="/vendu/:token" element={<VenduOneClickPage />} />
        <Route path="/mes-alertes" element={<ProtectedRoute><MesAlertesPage /></ProtectedRoute>} />
        <Route path="/messages" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
        <Route path="/messages/:id" element={<ProtectedRoute><ConversationPage /></ProtectedRoute>} />
        <Route path="/parametres" element={<ProtectedRoute><ParametresPage /></ProtectedRoute>} />
        <Route path="/admin/hotels" element={<ProtectedRoute><AdminHotelsPage /></ProtectedRoute>} />
        <Route path="/admin/hotels/:id/disponibilites" element={<ProtectedRoute><AdminHotelAvailabilityPage /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute><AdminDashboardPage /></ProtectedRoute>} />
        <Route path="/admin/annonces" element={<ProtectedRoute><AdminAnnoncesPage /></ProtectedRoute>} />
        <Route path="/admin/securite" element={<ProtectedRoute><AdminSecurityPage /></ProtectedRoute>} />
        <Route path="/admin/investissements" element={<ProtectedRoute><AdminInvestmentsPage /></ProtectedRoute>} />
        <Route path="/admin/campagnes" element={<ProtectedRoute><AdminCampagnesPage /></ProtectedRoute>} />
        <Route path="/admin/guide-feedback" element={<ProtectedRoute><AdminGuideFeedbackPage /></ProtectedRoute>} />
        <Route path="/admin/financing" element={<ProtectedRoute><AdminFinancingPage /></ProtectedRoute>} />
        <Route path="/admin/paiements" element={<ProtectedRoute><AdminPaymentsPage /></ProtectedRoute>} />
        <Route path="/admin/escrows" element={<ProtectedRoute><AdminEscrowsPage /></ProtectedRoute>} />
        <Route path="/admin/sms" element={<ProtectedRoute><AdminSmsPage /></ProtectedRoute>} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <Footer />
      <PWAInstallPrompt />
      <BiometricInviteBanner />
      <PresenceHeartbeat />
      <OfflineBanner />
      <RgpdBanner />
      <AuthModal />
      <CompareFloatingDrawer />
      <Toaster position="top-center" richColors closeButton />
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <CountryProvider>
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
      </CountryProvider>
    </BrowserRouter>
  );
}

export default App;
